import pipe from 'callbag-pipe';
import subscribe from 'callbag-subscribe';

import {ShopifyService} from '../../../../api/services/shopify.service';
import {QuantityPickerView} from './quantity-picker.view';
import {InputEventUtils} from '../../../../utils/input_event_utils';
import {ShoppingCartService} from '../../../../api/services/shopping-cart.service';
import {AppUtils} from '../../../../utils/app_utils';
import {Product} from '../../../../interfaces/product.interface';
import {IdealCartService} from '../../../../api/services/ideal-cart.service';
import {IdealVariant, ShopifyVariant} from '../../../../interfaces/shopify-product.interface';
import {IdealService} from '../../../../api/services/ideal.service';

export class QuantityPicker {
  private increaseButton: HTMLElement;
  private decreaseButton: HTMLElement;
  private quantityLabel: HTMLElement;
  private limitationLabel: HTMLElement;
  variant: ShopifyVariant | IdealVariant;
  quantity: number = 1;
  availableQuantity: number = 0;
  private readonly selectedProductSubjectRef: Function;

  constructor(
    readonly product: Product,
    readonly mainContainer: HTMLElement,
    readonly shopifyService: ShopifyService | IdealService,
    readonly shoppingCartService: ShoppingCartService | IdealCartService
  ) {
    this.changeQuantity = this.changeQuantity.bind(this);
    this.initComponent();

    const quantityButtons = this.mainContainer.querySelectorAll<HTMLElement>(
      '.ecommerce-product-details-item-quantity-button'
    );
    quantityButtons.forEach(button => {
      InputEventUtils.addSelectEvents(button, this.changeQuantity);
    });

    this.selectedProductSubjectRef = pipe(
      this.shopifyService.selectedProductSubject,
      subscribe({
        next: (variant: ShopifyVariant) => {
          if (variant) {
            this.variant = variant;
            this.quantity = 1;
            this.updateAvailableQuantity();
            this.updateQuantityLabel();
            this.updateDisableState();
          }
        }
      })
    );
  }

  get product_quantity() {
    return this.quantity;
  }

  get per_order_limit() {
    return (this.variant as ShopifyVariant)?.per_order_limit;
  }

  get product_variant_id() {
    return (this.variant as ShopifyVariant)?.variant_id;
  }

  get product_variant_inventory_quantity() {
    return this.per_order_limit || this.variant?.inventory_quantity;
  }

  get product_variant_available_quantity() {
    return this.availableQuantity;
  }

  private initComponent() {
    const quantityPickerContainer = this.mainContainer.querySelector<HTMLElement>('.app-ecommerce-quantity-picker-container');
    const quantityPicker = document.createElement('div');
    quantityPickerContainer.append(quantityPicker);
    quantityPicker.outerHTML = QuantityPickerView(this.product?.per_order_limit);
    this.increaseButton = quantityPickerContainer.querySelector<HTMLElement>('.app-shopping-cart-item-increase');
    this.decreaseButton = quantityPickerContainer.querySelector<HTMLElement>('.app-shopping-cart-item-decrease');
    this.quantityLabel = quantityPickerContainer.querySelector<HTMLElement>('.ecommerce-product-details-item-quantity-value');
    this.limitationLabel = quantityPickerContainer.querySelector<HTMLElement>('.ecommerce-product-details-item-quantity-limit');
  }

  private changeQuantity(e) {
    const operation = e.target.classList.contains('app-shopping-cart-item-increase') ? 'increase' : 'decrease';
    switch (operation) {
      case 'increase': {
        if (this.quantity + 1 <= this.variant.inventory_quantity ||
          !this.variant.inventory_quantity && this.quantity + 1 <= this.variant.quantity
        ) {
          if (this.per_order_limit && this.quantity + 1 > this.per_order_limit) {
            return;
          }
          this.quantity++;
          this.updateQuantityLabel();
        }
        break;
      }
      case 'decrease': {
        if (this.quantity - 1 >= 1) {
          this.quantity--;
          this.updateQuantityLabel();
        }
        break;
      }
    }
    this.updateDisableState();
  }

  private updateQuantityLabel() {
    this.quantityLabel.innerText = this.quantity.toString();
    if (this.variant?.isAutoSelected) {
      this.limitationLabel?.classList.add('is-hidden');
    } else {
      this.limitationLabel?.classList.remove('is-hidden');
    }
  }

  private updateDisableState() {
    (this.per_order_limit && this.quantity >= this.per_order_limit) ||
    this.quantity >= this.variant?.inventory_quantity
      ? this.increaseButton.classList.add('disabled')
      : this.increaseButton.classList.remove('disabled');

    this.quantity <= 1
      ? this.decreaseButton.classList.add('disabled')
      : this.decreaseButton.classList.remove('disabled');
  }

  updateAvailableQuantity() {
    const variantsWithSameId = (this.shoppingCartService as ShoppingCartService).shoppingCart?.cart.filter(
      item => item.merchandiseId === this.product_variant_id
    );
    const overallQuantity = AppUtils.overallQuantity(
      this.shoppingCartService.shoppingCart,
      this.variant,
      this.per_order_limit
    );
    if (variantsWithSameId?.length) {
      if (this.per_order_limit) {
        this.availableQuantity = this.per_order_limit - overallQuantity;
        return;
      }
      this.availableQuantity = this.variant.inventory_quantity - overallQuantity;
    } else {
      if (this.per_order_limit) {
        this.availableQuantity = this.per_order_limit - overallQuantity;
        return;
      }
      this.availableQuantity = this.variant?.inventory_quantity;
    }
  }

  dispose() {
    this.selectedProductSubjectRef();
  }
}
